import React, { useState, useRef, useEffect, FC } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { IoIosArrowDown } from "react-icons/io";
import { HiOutlineBell } from "react-icons/hi2";
import { Badge } from "antd";
import { useNotificationStore, useProfileStore } from "store";
import { NotificationDropdown } from "../NotificationDropdown";
import { routeNames } from "utils";

interface MenuItemProps {
  active?: boolean;
}

const StickyHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 1000;
  background: white;
`;

const Nav = styled(motion.nav)`
  background: white;
  border-bottom: 1px solid #e5e7eb;
  padding: 1rem 1.5rem;
  font-family: "Manrope", sans-serif;

  @media (max-width: 768px) {
    border-bottom: none;
  }
`;

const NavContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1440px;
  margin: 0 auto;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const Logo = styled(motion.img)`
  height: 32px;
`;

const NavigationLinks = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1.5rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(motion(Link))<MenuItemProps>`
  text-decoration: none;
  font-size: 14px;
  color: ${(props) => (props.active ? "#5575F7" : "#6B7280")};
  font-weight: ${(props) => (props.active ? "500" : "400")};
  transition: color 0.2s;

  &:hover {
    color: #5575f7;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;

  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownIndicator = styled(motion.span)`
  margin-left: 5px;
  font-size: 10px;
  margin-top: 3px;
`;

const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 0;
  background-color: white;
  min-width: 160px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  padding: 0.5rem 0;
  z-index: 1001;
`;

const DropdownItem = styled(motion.div)`
  padding: 0.5rem 1rem;
  color: #000000;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    background-color: #f5f5f5;
    color: #5575f7;
  }
`;

const UserDropdownMenu = styled(DropdownMenu)`
  right: 0;
  left: auto;
  min-width: 200px;
`;

const UserDropdownItem = styled(DropdownItem)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const UserInfo = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;

  h4 {
    margin: 0;
    font-size: 14px;
    font-weight: 500;
  }

  p {
    margin: 0;
    font-size: 12px;
    color: #6b7280;
  }
`;

const LanguageSelector = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 14px;
  color: #6b7280;
  padding: 0.25rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NotificationButton = styled(motion.button)`
  position: relative;
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #5575f7;
  }
`;

const UserInitials = styled(motion.div)`
  width: 32px;
  height: 32px;
  background: #5575f7;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
  font-weight: 500;
  font-family: "Manrope", sans-serif;
`;

const UserMenu = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
`;

const MobileTabBar = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    border-bottom: 1px solid #e5e7eb;
  }
`;

const MobileTab = styled(motion(Link))<MenuItemProps>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.9rem 0.5rem;
  text-decoration: none;
  font-size: 13px;
  font-weight: ${(props) => (props.active ? "600" : "500")};
  color: ${(props) => (props.active ? "#5575F7" : "#6B7280")};
  position: relative;
  transition: color 0.2s;

  &:hover {
    color: #5575f7;
  }
`;

const MobileTabIndicator = styled(motion.span)`
  position: absolute;
  bottom: 0;
  left: 25%;
  right: 25%;
  height: 3px;
  background: #5575f7;
  border-radius: 3px 3px 0 0;
`;

export interface NavbarProps {
  onLogout: () => void;
}
export const Navbar:FC<NavbarProps> = ({
  onLogout
}) => {
  const location = useLocation();
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { user, getName, getInitials } = useProfileStore();
  const navigate = useNavigate();

  const languageRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const languages = ["English", "Español", "Français"];
  const { isOpen, setIsOpen, notifications } = useNotificationStore();
  const unreadCount = notifications.filter((n) => !n.read).length;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        languageRef.current &&
        !languageRef.current.contains(event.target as Node)
      ) {
        setIsLanguageOpen(false);
      }
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const isActive = (path: string) => location.pathname === path;

  const isTabActive = (to: string) => {
    if (to === routeNames.interviews) {
      return (
        location.pathname === routeNames.interviews ||
        location.pathname.startsWith("/d/interview/")
      );
    }
    return location.pathname === to;
  };

  const navVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.5 },
    },
  };

  const dropdownVariants = {
    hidden: {
      opacity: 0,
      y: -10,
      height: 0,
    },
    visible: {
      opacity: 1,
      y: 0,
      height: "auto",
      transition: {
        duration: 0.3,
        staggerChildren: 0.05,
      },
    },
    exit: {
      opacity: 0,
      y: -10,
      height: 0,
      transition: {
        duration: 0.2,
      },
    },
  };

  const dropdownItemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.2 },
    },
  };

  return (
    <StickyHeader>
      <Nav initial="hidden" animate="visible" variants={navVariants}>
      <NavContainer>
        <LeftSection>
          <Link to={"#"} onClick={() => window.location.href = routeNames.home}>
            <Logo
              src="/assets/logo.png"
              alt="compellingcandidature"
              variants={itemVariants}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            />
          </Link>
          <NavigationLinks variants={itemVariants}>
            <NavLink
              to={routeNames.dashboard}
              active={isActive(routeNames.dashboard)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Courses
            </NavLink>
            <NavLink
              to={routeNames.interviews}
              active={isActive(routeNames.interviews)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Interviews
            </NavLink>
            <NavLink
              to={routeNames.progress}
              active={isActive(routeNames.progress)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Progress tracker
            </NavLink>
          </NavigationLinks>
        </LeftSection>

        <RightSection>
          <ActionButtons>
            <DropdownContainer ref={languageRef}>
              {/* <LanguageSelector
                onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>English</span>
                <DropdownIndicator
                  animate={{ rotate: isLanguageOpen ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <IoIosArrowDown size={16} />
                </DropdownIndicator>
              </LanguageSelector> */}

              <AnimatePresence>
                {isLanguageOpen && (
                  <DropdownMenu
                    variants={dropdownVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    {languages.map((lang, index) => (
                      <DropdownItem
                        key={index}
                        variants={dropdownItemVariants}
                        whileHover={{ x: 5 }}
                      >
                        {lang}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                )}
              </AnimatePresence>
            </DropdownContainer>

            <NotificationButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsOpen(!isOpen)}
            >
              <Badge count={unreadCount} size="small">
                <HiOutlineBell size={20} />
              </Badge>
              <NotificationDropdown />
            </NotificationButton>

            <DropdownContainer ref={userMenuRef}>
              <UserMenu
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <UserInitials>{getInitials()}</UserInitials>
                <DropdownIndicator
                  animate={{ rotate: isUserMenuOpen ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <IoIosArrowDown size={16} color="#6B7280" />
                </DropdownIndicator>
              </UserMenu>

              <AnimatePresence>
                {isUserMenuOpen && (
                  <UserDropdownMenu
                    variants={dropdownVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    <UserInfo>
                      <h4>{getName()}</h4>
                      <p>{user?.email}</p>
                    </UserInfo>
                    <UserDropdownItem
                      variants={dropdownItemVariants}
                      whileHover={{ x: 5 }}
                      onClick={() => navigate(routeNames.profile)}
                    >
                      Account Settings
                    </UserDropdownItem>
                    <UserDropdownItem
                      variants={dropdownItemVariants}
                      whileHover={{ x: 5 }}
                      style={{ color: "#EF4444" }}
                      onClick={onLogout}
                    >
                      Sign Out
                    </UserDropdownItem>
                  </UserDropdownMenu>
                )}
              </AnimatePresence>
            </DropdownContainer>

            </ActionButtons>
        </RightSection>
      </NavContainer>
      </Nav>

      <MobileTabBar>
        <MobileTab
          to={routeNames.dashboard}
          active={isTabActive(routeNames.dashboard)}
          whileTap={{ scale: 0.97 }}
        >
          Courses
          {isTabActive(routeNames.dashboard) && (
            <MobileTabIndicator layoutId="dashboard-tab-indicator" />
          )}
        </MobileTab>
        <MobileTab
          to={routeNames.interviews}
          active={isTabActive(routeNames.interviews)}
          whileTap={{ scale: 0.97 }}
        >
          Interviews
          {isTabActive(routeNames.interviews) && (
            <MobileTabIndicator layoutId="dashboard-tab-indicator" />
          )}
        </MobileTab>
        <MobileTab
          to={routeNames.progress}
          active={isTabActive(routeNames.progress)}
          whileTap={{ scale: 0.97 }}
        >
          Progress tracker
          {isTabActive(routeNames.progress) && (
            <MobileTabIndicator layoutId="dashboard-tab-indicator" />
          )}
        </MobileTab>
      </MobileTabBar>
    </StickyHeader>
  );
};
